/**
 * External Imports
 */
import { FC, useMemo, useState } from 'react';
import { connect } from 'react-redux';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { orderBy } from 'lodash';
import { format } from 'date-fns';

/**
 * Nexus Imports
 */
import { NeuAvatar, NeuImage } from '@neutron/react';
import { useAnalyticsApi } from '@shared-web-analytics/react/dist';

/**
 * Internal Imports
 */
import Table from '../../../shared/table';

import { RootState } from '../../../../redux/store';
import { useGetSassTokenQuery } from '../../../../redux/apis/Sass.api';

import {
  logListClick,
  logRoundProfile
} from '../../../../utils/analyticsHelpers';
import { useDebounceValue } from '../../../../utils/debouncers';
import {
  date,
  handleBlobPicUrl,
  userProfilePicUrl
} from '../../../../utils/helpers';

/**
 * Global Type Definition Imports
 */
import { Employee, RoundHistory } from '../../../../config/interfaces';

/**
 * Style Imports
 */
import {
  CellContainer,
  StyledLabel,
  StyledNeuTableRow
} from '../../../shared/table/Table.styles';
import {
  AvatarDiv,
  CompleteIcon,
  EmployeeInfoDiv,
  EmployeeAvatar,
  IconDiv,
  LastValidationLabel,
  OverdueImg,
  ToDoIcon
} from './AIDETListTable.styles';

/**
 * Static Asset Imports
 */
import defaultImage from '../../../../assets/images/defaultImage.png';
import overdueLogo from '../../../../assets/images/alarm.png';
import competentIcon from '../../../../assets/images/competent_icon.png';
import developmentIcon from '../../../../assets/images/development_icon.png';
import expertIcon from '../../../../assets/images/expert_icon.png';

/**
 * Constant Imports
 */
import validationCompetencies from '../../../../constants/validValidationCompetencies.json';

interface AIDETListTableProps {
  isLoading: boolean;
  searchTerm: string;
  validationFilter: string;
  validationRounds: (Employee & RoundHistory)[];
}

const AIDETListTable: FC<AIDETListTableProps> = ({
  isLoading,
  searchTerm,
  validationFilter,
  validationRounds
}) => {
  const [sortKey, setSortKey] = useState<string>('endDueDate');
  const [sortDir, setSortDir] = useState<'desc' | 'asc'>('asc');
  const [active, setActive] = useState<string>('Round Due');

  const { logTrackingEvent } = useAnalyticsApi();

  const dbSearchTerm = useDebounceValue(searchTerm, 250);

  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { '*': view } = useParams();

  const {
    data: sasTokenData,
    // isError: isGetSassTokenError,
    isFetching: isGetSassTokenFetching
    // isLoading: isGetSassTokenLoading
  } = useGetSassTokenQuery();

  const handleSort = (column: string) => {
    let key;
    switch (column) {
      case 'Employee':
        key = 'fullName';
        break;
      case 'Department':
        key = 'dept';
        break;
      case 'Validated By':
        key = 'lastRoundedBy';
        break;
      case 'Last Validated':
        key = 'lastRoundDate';
        break;
      case 'Validation Due':
        key = 'endDueDate';
        break;
      default:
        key = '';
    }
    if (key === sortKey && sortDir === 'asc') {
      setSortDir('desc');
    } else {
      setSortDir('asc');
    }
    setActive(column);
    setSortKey(key);
  };

  const openEmployeeProfile = (employee: Employee) => {
    logTrackingEvent([
      ...logListClick('table', view),
      ...logRoundProfile('aidetValidation')
    ]);
    navigate('/validation/profile/aidet', {
      state: {
        from: pathname,
        employeeId: employee.hcaid,
        mode: 'aidet'
      }
    });
  };

  const filteredValidationRounds = useMemo(() => {
    const q = new RegExp(dbSearchTerm.trim().replace(/\\/g, ''), 'i');

    const validationRoundsFilteredBySearch = validationRounds.filter(
      employee =>
        `${employee.firstName} ${employee.lastName}`.match(q) ||
        employee.hcaid.match(q) ||
        `${employee.lastName} ${employee.firstName}`.match(q)
    );

    const validationRoundSubjectsAndAdHoc =
      validationRoundsFilteredBySearch.reduce(
        (
          acc: {
            validationSubjects: (Employee & RoundHistory)[];
            adHoc: (Employee & RoundHistory)[];
          },
          employee: Employee & RoundHistory
        ) =>
          employee.adHoc
            ? { ...acc, adHoc: [...acc.adHoc, employee] }
            : {
                ...acc,
                validationSubjects: [...acc.validationSubjects, employee]
              },
        { validationSubjects: [], adHoc: [] }
      );

    const validationSubjectsToRound =
      validationRoundSubjectsAndAdHoc.validationSubjects;
    const validationAdHocsToRound = validationRoundSubjectsAndAdHoc.adHoc;

    switch (validationFilter) {
      case 'completed':
        return [
          [],
          [
            ...validationSubjectsToRound.filter(
              employee => employee?.complianceStatus === 'Completed'
            ),
            ...validationAdHocsToRound
          ]
        ];
      case 'to-do':
        return [
          validationSubjectsToRound.filter(
            employee =>
              employee?.complianceStatus === 'Todo' ||
              employee?.complianceStatus === 'Overdue' ||
              !employee.complianceStatus
          ),
          []
        ];
      case 'expert':
        return [
          validationSubjectsToRound.filter(
            employee =>
              employee.lastRound?.tag.includes('Validation_AIDET_E') &&
              employee?.complianceStatus !== 'Completed'
          ),
          validationSubjectsToRound.filter(
            employee =>
              employee.lastRound?.tag.includes('Validation_AIDET_E') &&
              employee?.complianceStatus === 'Completed'
          )
        ];
      case 'competent':
        return [
          validationSubjectsToRound.filter(
            employee =>
              employee.lastRound?.tag.includes('Validation_AIDET_C') &&
              employee?.complianceStatus !== 'Completed'
          ),
          validationSubjectsToRound.filter(
            employee =>
              employee.lastRound?.tag.includes('Validation_AIDET_C') &&
              employee?.complianceStatus === 'Completed'
          )
        ];
      case 'developing':
        return [
          validationSubjectsToRound.filter(
            employee =>
              employee.lastRound?.tag.includes('Validation_AIDET_D') &&
              employee?.complianceStatus !== 'Completed'
          ),
          validationSubjectsToRound.filter(
            employee =>
              employee.lastRound?.tag.includes('Validation_AIDET_D') &&
              employee?.complianceStatus === 'Completed'
          )
        ];
      default:
        return [
          validationSubjectsToRound.filter(
            employee => employee?.complianceStatus !== 'Completed'
          ),
          validationSubjectsToRound.filter(
            employee => employee?.complianceStatus === 'Completed'
          )
        ];
    }
  }, [dbSearchTerm, validationFilter, validationRounds]);

  const sortedValidationRounds = useMemo(() => {
    const filteredValidationRoundsCopy = [...filteredValidationRounds];
    const orderBySortDir = sortDir === 'asc' ? 'asc' : 'desc';
    return [
      filteredValidationRoundsCopy[0].length > 0
        ? orderBy(
            filteredValidationRoundsCopy[0],
            [round => (round as any)[sortKey]?.toLowerCase() || ''],
            [orderBySortDir]
          )
        : filteredValidationRoundsCopy[0],
      filteredValidationRoundsCopy[1].length > 0
        ? orderBy(
            filteredValidationRoundsCopy[1],
            [round => (round as any)[sortKey]?.toLowerCase() || ''],
            [orderBySortDir]
          )
        : filteredValidationRoundsCopy[1]
    ];
  }, [filteredValidationRounds, sortDir, sortKey]);

  const isTableEmpty = useMemo(
    () => !dbSearchTerm && sortedValidationRounds.flat().length === 0,
    [dbSearchTerm, sortedValidationRounds]
  );

  const isTableEmptyCompleted = useMemo(
    () =>
      !dbSearchTerm &&
      sortedValidationRounds.flat().length === 0 &&
      validationFilter === 'Completed',
    [dbSearchTerm, sortedValidationRounds, validationFilter]
  );

  const isTableSearchEmpty = useMemo(
    () => dbSearchTerm && sortedValidationRounds.flat().length === 0,
    [dbSearchTerm, sortedValidationRounds]
  );

  // const cols =
  //   '{ "Round Due":"17%" }';

  const headers: JSX.Element[] = [
    <CellContainer
      key="Employee"
      data-testid="AIDET-Table-Employee-Column"
      unit="column"
      colWidth={22}
      direction={!(active === 'Employee') ? 'none' : sortDir}
      sort
    >
      <span className="text-clamp text-clamp__1 break-all">Employee</span>
    </CellContainer>,
    <CellContainer
      key="Department"
      data-testid="AIDET-Table-Department-Column"
      unit="column"
      colWidth={21}
      direction={!(active === 'Department') ? 'none' : sortDir}
      sort
    >
      <span className="text-clamp text-clamp__1 break-all">Department</span>
    </CellContainer>,
    <CellContainer
      key="Validated By"
      data-testid="AIDET-Table-Rounded-By-Column"
      unit="column"
      colWidth={21}
      direction={!(active === 'Validated By') ? 'none' : sortDir}
      sort
    >
      <span className="text-clamp text-clamp__1 break-all">Validated By</span>
    </CellContainer>,
    <CellContainer
      key="Last Validated"
      data-testid="AIDET-Table-Last-Validated-Column"
      unit="column"
      colWidth={19}
      direction={!(active === 'Last Validated') ? 'none' : sortDir}
      sort
    >
      <span className="text-clamp text-clamp__1 break-all">Last Validated</span>
    </CellContainer>,
    <CellContainer
      key="Validation Due"
      id="AIDET-Table-Validation-Due-Column"
      unit="column"
      colWidth={17}
      direction={!(active === 'Validation Due') ? 'none' : sortDir}
      sort
    >
      Validation Due
    </CellContainer>
  ];

  const rows = sortedValidationRounds
    .flat()
    .map((employee: Employee & RoundHistory, i: number) => {
      const competencyTag = employee.adHoc
        ? employee?.tag
        : employee?.lastRound?.tag;

      const competencyIcon = competencyTag?.includes('Validation_AIDET_E')
        ? expertIcon
        : competencyTag?.includes('Validation_AIDET_C')
        ? competentIcon
        : competencyTag?.includes('Validation_AIDET_D')
        ? developmentIcon
        : '';

      return (
        <StyledNeuTableRow // eslint-disable-next-line react/no-array-index-key
          key={`AIDET-List-Row-${i}`}
          data-testid={`AIDET-List-Row-${employee.hcaid}`}
          unit="row"
          size="default"
          height="6.5em"
          role="button"
          styles={
            employee.complianceStatus === 'Completed'
              ? 'background-color: #f6f6f6 !important'
              : ''
          }
          onClick={() => openEmployeeProfile(employee)}
        >
          <CellContainer unit="column" colWidth={22}>
            <EmployeeInfoDiv className="h-100 px-0">
              <IconDiv>
                {employee.complianceStatus === 'Completed' ? (
                  <CompleteIcon color="blue">check</CompleteIcon>
                ) : employee.complianceStatus === 'Overdue' ? (
                  <OverdueImg width="20" alt="overdue logo" src={overdueLogo} />
                ) : (
                  <ToDoIcon color="orange">circle</ToDoIcon>
                )}
              </IconDiv>
              <AvatarDiv>
                <NeuAvatar
                  className="me-1"
                  unit="avatar"
                  color="gray"
                  style={{ height: '40px', width: '40px' }}
                >
                  {sasTokenData ? (
                    <EmployeeAvatar
                      src={
                        handleBlobPicUrl(
                          employee.pictureUrl,
                          sasTokenData.sasToken
                        ) ??
                        userProfilePicUrl(employee.hcaid, sasTokenData.sasToken)
                      }
                      alt="Employee Image"
                      onError={(e: any) => {
                        e.currentTarget.src = defaultImage;
                        return null;
                      }}
                    />
                  ) : (
                    <EmployeeAvatar src={defaultImage} alt="Default Image" />
                  )}
                </NeuAvatar>
              </AvatarDiv>
              <div>
                <StyledLabel className="neu-text-blue-70 bold">
                  {employee.firstName} {employee.lastName}
                </StyledLabel>
                <StyledLabel>{employee.hcaid}</StyledLabel>
                <StyledLabel className="text-clamp text-clamp__1">
                  {employee.title}
                </StyledLabel>
              </div>
            </EmployeeInfoDiv>
          </CellContainer>
          <CellContainer unit="column" colWidth={21}>
            <StyledLabel className="neu-text-gray-90">
              {employee.adHoc ? employee.employeeDept : employee.dept}
            </StyledLabel>
          </CellContainer>
          <CellContainer unit="column" colWidth={21}>
            <StyledLabel>
              {employee.lastRoundDate &&
              employee.lastRoundedBy &&
              employee.lastRoundedBy !== 'Unknown'
                ? employee.lastRoundedBy
                : employee.lastRoundDate &&
                  employee.lastRoundedBy &&
                  employee.lastRoundedBy === 'Unknown'
                ? 'Unknown'
                : ''}
            </StyledLabel>
            <StyledLabel>
              {(employee.lastRound &&
                (employee.lastRound?.roundedById || '')) ||
                (employee.lastRoundedById ?? '')}
            </StyledLabel>
            <StyledLabel className="text-clamp text-clamp__1">
              {(employee.lastRound &&
                (employee.lastRound?.roundedByTitle || '')) ||
                (employee.lastRoundedById &&
                  (employee.lastRoundedByTitle || '')) ||
                ''}
            </StyledLabel>
          </CellContainer>
          <CellContainer unit="column" colWidth={19}>
            <div className="d-flex align-items-center">
              <LastValidationLabel>
                {employee.lastRoundedBy &&
                employee.lastRoundedBy !== 'Unknown' &&
                (!employee.lastRoundDate ||
                  (employee.lastRoundDate &&
                    !date.isOrbitTimestamp(employee.lastRoundDate)))
                  ? 'Unknown'
                  : employee.lastRoundedBy && employee.lastRoundDate
                  ? format(date.parseDate(employee.lastRoundDate), 'MM/dd/yyyy') // make unknown if name and no date
                  : ''}
              </LastValidationLabel>
              {competencyTag &&
                validationCompetencies.includes(competencyTag) && (
                  <NeuImage
                    width="24"
                    alt="competency logo"
                    src={competencyIcon}
                  />
                )}
            </div>
          </CellContainer>
          <CellContainer unit="column" colWidth={17}>
            <StyledLabel>
              {date.datePrettyString(employee.endDueDate)}
            </StyledLabel>
          </CellContainer>
        </StyledNeuTableRow>
      );
    });

  return (
    <Table
      customClass="validation-list-table"
      headers={headers}
      rows={rows}
      isTableEmpty={isTableEmpty}
      isTableEmptySearch={isTableSearchEmpty}
      isTableEmptyCompleted={isTableEmptyCompleted}
      loading={isLoading || isGetSassTokenFetching}
      type="validationList"
      onSort={(e: any) => handleSort(e.detail.label)}
    />
  );
};

const mapReduxStateToProps = (state: RootState) => ({
  isLoading: state.EmployeeReducer.isLoadingSubjectsToRoundForList
});

export default connect(mapReduxStateToProps)(AIDETListTable);
